import React, { useEffect, useState } from 'react';
import './Button.scss';
import ImagePreview from './ImagePreview';
import ImageUploader from './ImageUploader';
import { SessionWelcomeDetails, SetSessionWelcomeRequest } from '../models/api';
import { getWelcome, updateWelcome } from '../services/session.service';
import { getDefault } from '../services/icon.service';
import LoadingSpinner from './LoadingSpinner';
import { ResponseData } from '../services/models';
import { getUrlParams, removeUrlParam } from '../services/parameter.service';
import { useRefresh } from '../contexts/RefreshContext';
import { FeatureGate } from './FeatureGate';

type SessionWelcomeEditorProps = {
  sessionReference: string;
  setMessage: (message: string) => void;
  setErrors: (errors: string[]) => void;
};

interface LoadingState {
  welcomeDelete: boolean;
  iconDelete: boolean;
  iconToggle: boolean;
}

const SessionWelcomeEditor: React.FC<SessionWelcomeEditorProps> = ({
  sessionReference,
  setMessage,
  setErrors
}) => {
  const { refreshKey, triggerRefresh } = useRefresh();
  const [loading, setLoading] = useState(true);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    iconDelete: false,
    welcomeDelete: false,
    iconToggle: false
  });

  const [existingDefaultIconFileName, setExistingDefaultIconFileName] =
    useState<string | null>(null);

  const [existingDefaultIconBlobName, setExistingDefaultIconBlobName] =
    useState<string | null>(null);

  const [useNoIcon, setUseNoIcon] = useState(false);
  const [previewLink, setPreviewLink] = useState('');
  const { created } = getUrlParams();

  useEffect(() => {
    if (created) {
      setMessage(
        'Event created.  You can now begin adding and editing page content.'
      );
      removeUrlParam('created');
    }
  }, []);

  const getWelcomeData = async (
    input?: ResponseData<SessionWelcomeDetails> | undefined
  ): Promise<boolean> => {
    setLoading(true);

    try {
      const result = input ?? (await getWelcome(sessionReference));

      if (result.success && result.data) {
        triggerRefresh();
        setUseNoIcon(result.data.useNoIcon);
        setExistingWelcomeImageFileName(result.data.fileName);
        setExistingWelcomeImageBlobName(result.data.blobName);
        setPreviewLink(result.data.previewLink);
        if (result.data.icon.hasIcon) {
          setExistingWelcomeIconFileName(result.data.icon.fileName);
          setExistingWelcomeIconBlobName(result.data.icon.blobName);
        } else {
          setExistingWelcomeIconFileName(null);
          setExistingWelcomeIconBlobName(null);
        }
      } else {
        setErrors(result.errors);
        return false;
      }
      return true;
    } finally {
      setLoading(false);
    }
  };
  const getDefaultIcon = async () => {
    const result = await getDefault();

    if (result.success && result.data) {
      if (result.data.hasIcon) {
        setExistingDefaultIconFileName(result.data.fileName);
        setExistingDefaultIconBlobName(result.data.blobName);
      }
    } else {
      setErrors(result.errors);
    }

    await getWelcomeData();
  };

  useEffect(() => {
    getDefaultIcon();
  }, []);

  const [existingWelcomeImageFileName, setExistingWelcomeImageFileName] =
    useState<string | null>(null);
  const [existingWelcomeImageBlobName, setExistingWelcomeImageBlobName] =
    useState<string | null>(null);

  const [existingWelcomeIconFileName, setExistingWelcomeIconFileName] =
    useState<string | null>(null);
  const [existingWelcomeIconBlobName, setExistingWelcomeIconBlobName] =
    useState<string | null>(null);

  const handleSaveWelcomeImage = async (
    selectedFile: File | null,
    selectedFileContents: string | null
  ) => {
    setErrors([]);

    if (!selectedFile || !selectedFileContents) {
      setErrors(['Cannot load image']);
      return false;
    }

    const request: SetSessionWelcomeRequest = {
      image: {
        fileName: selectedFile.name,
        fileContents: selectedFileContents,
        removeImage: false
      }
    };

    const result = await updateWelcome(sessionReference, request);

    if (result.success && result.data) {
      setExistingWelcomeImageFileName(result.data.fileName);
      setExistingWelcomeImageBlobName(result.data.blobName);

      setMessage('Image updated');
      await getWelcomeData();
      return true;
    } else {
      setErrors(result.errors);
    }

    return false;
  };
  const handleSaveIconImage = async (
    selectedFile: File | null,
    selectedFileContents: string | null
  ) => {
    setErrors([]);

    if (!selectedFile || !selectedFileContents) {
      setErrors(['Cannot load image']);
      return false;
    }

    const request: SetSessionWelcomeRequest = {
      icon: {
        fileName: selectedFile.name,
        fileContents: selectedFileContents,
        removeImage: false
      }
    };

    const result = await updateWelcome(sessionReference, request);

    if (result.success && result.data) {
      setExistingWelcomeIconFileName(result.data.fileName);
      setExistingWelcomeIconBlobName(result.data.blobName);

      setMessage('Icon updated');
      await getWelcomeData();
      return true;
    } else {
      setErrors(result.errors);
    }

    return false;
  };

  const removeWelcomeImage = async () => {
    setLoadingState((p) => ({ ...p, welcomeDelete: true }));

    const request: SetSessionWelcomeRequest = {
      image: {
        removeImage: true
      }
    };

    const result = await updateWelcome(sessionReference, request);

    if (await getWelcomeData(result)) {
      setMessage('Image removed');
    }

    setLoadingState((p) => ({ ...p, welcomeDelete: false }));
  };

  const removeIconImage = async () => {
    setLoadingState((p) => ({ ...p, iconDelete: true }));

    const request: SetSessionWelcomeRequest = {
      icon: {
        removeImage: true
      }
    };

    const result = await updateWelcome(sessionReference, request);

    if (await getWelcomeData(result)) {
      setMessage('Icon removed');
    }

    setLoadingState((p) => ({ ...p, iconDelete: false }));
  };

  const handleIconFlag = async (value: boolean) => {
    setLoadingState((p) => ({ ...p, iconToggle: true }));

    const request: SetSessionWelcomeRequest = {
      useNoIcon: value
    };

    const result = await updateWelcome(sessionReference, request);

    if (await getWelcomeData(result)) {
      setMessage(value ? 'Icon hidden' : 'Icon shown');
    }

    setLoadingState((p) => ({ ...p, iconToggle: false }));
  };

  if (loading) {
    return <LoadingSpinner inline={true} />;
  }

  return (
    <>
      <h2>Welcome screen</h2>
      <p>
        This screen will show while guests are waiting for the event to start.
      </p>
      <FeatureGate feature="WelcomePageBackground">
        <h3>Header image</h3>
        <div className="form-group">
          <p className="notes">
            Image should be no bigger than 5mb, and at least 6000px x 400px
          </p>
          <form className="content-form">
            <ImageUploader
              onSave={handleSaveWelcomeImage}
              buttonText="Save"
              existingWelcomeImageFileName={existingWelcomeImageFileName}
              existingWelcomeImageBlobName={existingWelcomeImageBlobName}
              removeWelcomeImage={removeWelcomeImage}
              isDeleting={loadingState.welcomeDelete}
            />
          </form>
        </div>
      </FeatureGate>

      <FeatureGate feature="WelcomePageLogo">
        <h3>Header logo</h3>
        <div className="form-group">
          <p className="notes">
            Logo should be no bigger than 5mb, and at least 300px x 300px
          </p>
          <form className="content-form">
            {!existingWelcomeIconBlobName && existingDefaultIconBlobName && (
              <>
                {!useNoIcon && (
                  <>
                    <p>Default logo currently selected</p>
                    <ImagePreview
                      existingFileName={existingDefaultIconFileName}
                      existingBlobName={existingDefaultIconBlobName}
                    />
                  </>
                )}
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="useNoIcon"
                    checked={useNoIcon}
                    onChange={(e) => handleIconFlag(e.target.checked)}
                  />
                  <label htmlFor="useNoIcon">Hide default icon</label>
                </div>
              </>
            )}
            {!existingWelcomeIconBlobName && !existingDefaultIconBlobName && (
              <>
                <p>No logo currently selected</p>
              </>
            )}
            <ImageUploader
              onSave={handleSaveIconImage}
              buttonText="Save"
              existingWelcomeImageFileName={existingWelcomeIconFileName}
              existingWelcomeImageBlobName={existingWelcomeIconBlobName}
              removeWelcomeImage={removeIconImage}
              isDeleting={loadingState.iconDelete}
            />
          </form>
        </div>
      </FeatureGate>
      <h3>Preview</h3>

      <div className="preview-content">
        <iframe src={previewLink + '/start'} key={refreshKey} title="Preview" />
      </div>
    </>
  );
};

export default SessionWelcomeEditor;
